import { CircleInfoIcon } from "@xola/icons";
import { flash, FormGroup, Input, Label, Modal, SubmitButton, Switch, Textarea, Tooltip } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import { requiredString } from "common";
import { FormikHelpers } from "formik";
import { get } from "lodash";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { Experience, ExperienceOption, ExperienceOptionDisplayType } from "types";
import { object } from "yup";
import { api } from "@/api/api";
import { NotifyCustomerCheckbox } from "@/components/Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLengthCounter } from "@/components/Field/ContentLengthCounter";
import { useInvalidate } from "@/hooks/useInvalidation";

interface EditExperienceOptionModalProps {
    experience: Experience;
    option?: ExperienceOption;
    isOpen: boolean;
    onClose: () => void;
}

export const EditExperienceOptionModal = ({ experience, option, isOpen, onClose }: EditExperienceOptionModalProps) => {
    const isSavingRef = useRef(false);

    const handleClose = () => {
        if (isSavingRef.current) {
            return;
        }
        onClose();
    };

    return (
        <Modal shouldCloseOnOutsideClick={false} isOpen={isOpen} onClose={handleClose}>
            <ModalContent experience={experience} option={option} isSavingRef={isSavingRef} onClose={onClose} />
        </Modal>
    );
};

const DISPLAY_TOOLTIP =
    "Highlight prominently visually marks the calendar and time slots so customers see this option upfront, before they make any date or time selection.";

// Matches ExperienceOption::$content's `@Assert\Length(max=280)` on the backend.
const EXPERIENCE_OPTION_MAX_CONTENT = 280;

const validationSchema = object().shape({
    name: requiredString(),
    content: requiredString().max(EXPERIENCE_OPTION_MAX_CONTENT, `Max ${EXPERIENCE_OPTION_MAX_CONTENT} characters`),
});

interface FormValues {
    name: string;
    content: string;
    displayType: ExperienceOptionDisplayType;
}

const ModalContent = ({
    experience,
    option,
    onClose,
    isSavingRef,
}: Except<EditExperienceOptionModalProps, "isOpen"> & { isSavingRef: React.MutableRefObject<boolean> }) => {
    const invalidate = useInvalidate();
    const originalContent = option?.content ?? "";
    const [shouldNotifyCustomer, setShouldNotifyCustomer] = useState(false);
    const handleNotifyChange = useMemoizedFn((event: React.ChangeEvent<HTMLInputElement>) =>
        setShouldNotifyCustomer(event.target.checked),
    );

    const { mutateAsync: save, isLoading } = useMutation(async (values: FormValues) => {
        const url = option?.id
            ? `/experiences/${experience.id}/options/${option.id}`
            : `/experiences/${experience.id}/options`;
        // Only the description reaches customers, so name/display-only edits never notify.
        const shouldEmailTravelers = shouldNotifyCustomer && values.content !== originalContent;
        const { data } = option?.id
            ? await api.put<ExperienceOption>(
                  url,
                  { ...values, _emailOverrides: { sendEmailToTraveler: shouldEmailTravelers } },
                  { suppressErrorToast: [409] },
              )
            : await api.post<ExperienceOption>(url, values, { suppressErrorToast: [409] });
        return data;
    });

    const handleSubmit = async (values: FormValues, { setErrors }: FormikHelpers<FormValues>) => {
        isSavingRef.current = true;
        try {
            await save(values);
            invalidate("experiences", experience.id);
            flash.success("Experience Option saved");
            onClose();
        } catch (error) {
            const reason = get(error, "response.data.global");
            if (get(error, "response.status") === 409 && reason?.[0] === "name_exists") {
                setErrors({ name: "An Experience Option with this name already exists" });
                return;
            }
            throw error;
        } finally {
            isSavingRef.current = false;
        }
    };

    return (
        <XolaFormik<FormValues>
            initialValues={{
                name: option?.name ?? "",
                content: originalContent,
                displayType: option?.displayType ?? ExperienceOptionDisplayType.STANDARD,
            }}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {({ values, errors, handleChange, setFieldValue, handleSubmit, dirty, isSubmitting }) => {
                const handleDisplayTypeChange = (isHighlighted: boolean) =>
                    setFieldValue(
                        "displayType",
                        isHighlighted ? ExperienceOptionDisplayType.HIGHLIGHTED : ExperienceOptionDisplayType.STANDARD,
                    );

                return (
                    <>
                        <Modal.Header>{option ? "Edit" : "Create"} Experience Option</Modal.Header>
                        <Modal.Body className="space-y-4">
                            <FormGroup>
                                <Label isError={!!errors.name} data-testid="experience-option-name-label">
                                    {errors.name ?? "Name"}
                                </Label>
                                <Input
                                    name="name"
                                    data-testid="experience-option-name"
                                    value={values.name}
                                    isError={!!errors.name}
                                    onChange={handleChange}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label isError={!!errors.content} data-testid="experience-option-content-label">
                                    {errors.content ?? "Description"}
                                </Label>
                                <Textarea
                                    name="content"
                                    className="h-24"
                                    data-testid="experience-option-content"
                                    value={values.content}
                                    maxLength={EXPERIENCE_OPTION_MAX_CONTENT}
                                    isError={!!errors.content}
                                    onChange={handleChange}
                                />
                                <div className="text-right">
                                    <ContentLengthCounter
                                        value={values.content}
                                        maxLength={EXPERIENCE_OPTION_MAX_CONTENT}
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label>
                                    Event Option Display{" "}
                                    <Tooltip content={DISPLAY_TOOLTIP}>
                                        <CircleInfoIcon />
                                    </Tooltip>
                                </Label>
                                <Switch.Group>
                                    <Switch
                                        size="medium"
                                        data-testid="experience-option-highlight-toggle"
                                        isChecked={values.displayType === ExperienceOptionDisplayType.HIGHLIGHTED}
                                        onChange={handleDisplayTypeChange}
                                    />
                                    <Switch.Label className="ml-2">Highlight prominently</Switch.Label>
                                </Switch.Group>
                            </FormGroup>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex items-center justify-between">
                                {option && values.content !== originalContent ? (
                                    <NotifyCustomerCheckbox
                                        isChecked={shouldNotifyCustomer}
                                        onChange={handleNotifyChange}
                                    />
                                ) : (
                                    <div />
                                )}
                                <div className="flex items-center space-x-4">
                                    <SecondaryButton
                                        data-testid="experience-option-cancel-button"
                                        disabled={isLoading || isSubmitting}
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </SecondaryButton>
                                    <SubmitButton
                                        data-testid="experience-option-save-button"
                                        isLoading={isLoading || isSubmitting}
                                        disabled={!dirty}
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>
                                </div>
                            </div>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};
